import { useEffect, useMemo, useState } from "react";
import { X, Instagram } from "lucide-react";
import { VIDEOS } from "@/lib/data";

// Bumped key so existing visitors get the new rotational popup once.
const STORAGE_KEY = "asym_intro_seen_v3";

function instagramEmbed(url) {
  if (!url) return null;
  const m = url.match(/instagram\.com\/(reel|p|tv)\/([\w-]+)/);
  return m ? `https://www.instagram.com/${m[1]}/${m[2]}/embed` : null;
}

export default function IntroVideoModal() {
  const [open, setOpen] = useState(false);

  // Pool of all welcome reels (any VIDEO entry with an instagramUrl).
  const pool = useMemo(
    () => VIDEOS.filter((v) => !!v.instagramUrl && !!instagramEmbed(v.instagramUrl)),
    [],
  );

  // Pick one at random for this visit. useMemo so the choice is stable
  // across re-renders within a single page load.
  const pick = useMemo(() => {
    if (pool.length === 0) return null;
    return pool[Math.floor(Math.random() * pool.length)];
  }, [pool]);

  useEffect(() => {
    let seen = false;
    try {
      seen = sessionStorage.getItem(STORAGE_KEY) === "1";
    } catch (e) {
      seen = false;
    }
    if (!seen && pick) {
      const t = setTimeout(() => {
        setOpen(true);
        try {
          sessionStorage.setItem(STORAGE_KEY, "1");
        } catch (e) {
          /* ignore */
        }
      }, 700);
      return () => clearTimeout(t);
    }
  }, [pick]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") close();
    }
    if (open) {
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", onKey);
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  function close() {
    setOpen(false);
    try {
      sessionStorage.setItem(STORAGE_KEY, "1");
    } catch (e) {
      /* ignore */
    }
  }

  if (!open || !pick) return null;

  const embedUrl = instagramEmbed(pick.instagramUrl);
  const reelUrl = pick.instagramUrl;
  const groupLabel =
    pick.group === "Committee"
      ? `${pick.role} · Aesthetic Symposium 2026`
      : `Faculty · Aesthetic Symposium 2026`;

  return (
    <div
      data-testid="intro-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center px-3 py-6 sm:px-6 animate-fade-up"
      role="dialog"
      aria-modal="true"
      aria-label={`A message from ${pick.name}`}
    >
      <button
        data-testid="intro-backdrop"
        aria-label="Close"
        onClick={close}
        className="absolute inset-0 bg-[#0B4636]/85 backdrop-blur-sm cursor-default"
      />

      <div className="relative w-full max-w-[420px] bg-[#F5F2EA] border border-[#C5A059]/40 shadow-2xl">
        <button
          data-testid="intro-close"
          aria-label="Close intro video"
          onClick={close}
          className="absolute -top-3 -right-3 sm:-top-4 sm:-right-4 z-10 w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center bg-[#1A1A1A] text-[#F5F2EA] hover:bg-[#0F5C46] transition-colors shadow-lg"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="px-5 sm:px-6 pt-5 pb-3 border-b border-[#C5A059]/25">
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#A4863F]">
            A message from
          </p>
          <p
            className="mt-1 font-serif text-xl sm:text-2xl text-[#1A1A1A] leading-snug"
            data-testid="intro-doctor-name"
          >
            {pick.name}
          </p>
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#0F5C46] mt-0.5">
            {groupLabel}
          </p>
        </div>

        <div
          className="relative w-full bg-black"
          style={{ aspectRatio: "9 / 16", maxHeight: "70vh" }}
        >
          <iframe
            src={embedUrl}
            title={`${pick.name} - Aesthetic Symposium 2026`}
            className="absolute inset-0 w-full h-full"
            allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
            allowFullScreen
            scrolling="no"
            frameBorder="0"
            data-testid="intro-reel-iframe"
          />
        </div>

        <div className="px-5 sm:px-6 py-4 flex flex-wrap items-center justify-between gap-3 border-t border-[#C5A059]/25">
          <a
            href={reelUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="intro-open-instagram"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#0F5C46] hover:text-[#1A1A1A]"
          >
            <Instagram className="w-3.5 h-3.5" />
            Open on Instagram
          </a>
          <button
            data-testid="intro-dismiss"
            onClick={close}
            className="bg-[#059669] hover:bg-[#0F5C46] text-white text-[11px] uppercase tracking-[0.22em] px-5 py-2.5 transition-colors"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}
